import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { PMField } from "../../../utils/PMField";
import { useDispatch } from "react-redux";
import PMSwitch from "../../../utils/PMSwitch";
import { DepartmentSelector } from "../../../utils/moduleSelectors/departmentSelector";
import UserChildrenTable from "./user-children-table";
import UserNextOfKinTable from "./user-next-of-kin-table";
import { dispatchWithToast } from "../../../Utils";
import { updateUserAsync } from "../../../../slices/user";
import { useCompanyHasModule } from "../../../../routes/roleOnly";
import { DocumentUploadSectionV5 } from "../../Media/DocumentUploadSectionV5";
import imgPlaceholder from "../../../../utils/img-placeholder.png";

export const UserInfo = (props) => {
  const { t } = useTranslation();
  const { item } = props;
  const dispatch = useDispatch();
  const websiteModuleActive = useCompanyHasModule("website");
  const isEmployee = !!item?.employment;
  const isEditable = item?.is_editable !== false; // Default to true if not set

  return (
    <>
      {!isEditable && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">
                {t("company.users.profile.read_only_title")}
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                {t("company.users.profile.read_only_multi_company")}
              </p>
            </div>
          </div>
        </div>
      )}
      <Formik
        enableReinitialize
        initialValues={{
          id: item?.id,
          name: item?.name,
          email: item?.email,
          mobile: item?.mobile,
          mobile_work: item?.mobile_work,
          birth_date: item?.birth_date,
          postal_address: item?.postal_address?.id
            ? item?.postal_address
            : {
                address: item?.address,
                address2: item?.address_two,
                postal: item?.postal,
                place: item?.place,
              },
          physical_address: item?.physical_address?.id
            ? item?.physical_address
            : "",
          nextCustomerNumber: item?.nextCustomerNumber || false,
          user_children: item?.user_children || [],
          user_next_of_kins: item?.user_next_of_kins || [],
          submit: null,
          active: item?.active,
          show_in_dropdown: item?.show_in_dropdown,
          show_on_website: item?.show_on_website,
          image_url: item?.image_url || "",
        }}
        validationSchema={Yup.object({
          name: Yup.string().max(255).required(t("customer.name.required")),
          description: Yup.string(),
        })}
        onSubmit={(values, helpers) => {
          try {
            const payload = {
              ...values,
              address: values?.postal_address?.address || "",
              postal: values?.postal_address?.postal || "",
              place: values?.postal_address?.place || "",
            };

            delete payload.physical_address;
            delete payload.short_description;

            dispatchWithToast(dispatch(updateUserAsync(payload)));

            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
          } catch (err) {
            toast.error("Nope");
            console.error(err);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          }
        }}
      >
            {(formik) => (
          <Form>
            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                {/* <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">{ t("Product Description") }</h3>
                    <p className="mt-1 text-sm text-gray-500"></p>
                  </div> */}
                <div className="grid sm:grid-cols-8 gap-6">
                  <div className="sm:col-span-2">
                    <PMSwitch label="Active" name="active" {...formik} />
                  </div>
                  <div className="sm:col-span-2">
                    <PMSwitch
                      label="Show in dropdown"
                      name="show_in_dropdown"
                      {...formik}
                    />
                  </div>
                <div className="sm:col-span-2">
                  <PMSwitch
                    label="Show on website"
                    name="show_on_website"
                    disabled={!websiteModuleActive || !isEmployee}
                    {...formik}
                  />
                </div>
                {(!websiteModuleActive || !isEmployee) && (
                  <div className="sm:col-span-4 flex items-center">
                    <button
                      type="button"
                      className="text-sm text-gray-500 underline"
                      onClick={() => {
                        const reason = !websiteModuleActive
                          ? "Website module is not active for this company. Enable it under Company > Features to use this setting."
                          : "User must be an employee to appear on the website. Go to the Employment tab and create an employment record.";
                        window.alert(reason);
                      }}
                    >
                      Why is this disabled?
                    </button>
                  </div>
                )}
                </div>
              </div>
            </div>

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {t("Detaljer")}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500"></p>
                </div>

                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="birth_date"
                      type="date"
                      label="Birthdate"
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                </div>
              </div>
            </div>

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {t("Contact information")}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500"></p>
                </div>

                <div className="grid grid-cols-6 gap-6 items-start">
                  <div className="col-span-6 sm:col-span-2">
                    <div className="flex items-center gap-2">
                      <img
                        src={formik.values.image_url || imgPlaceholder}
                        alt="Profile"
                        className="h-16 w-16 rounded-full object-cover ring-1 ring-gray-200"
                      />
                      <DocumentUploadSectionV5
                        multiple={false}
                        module="user"
                        moduleParam={item?.id}
                        required={false}
                        buttonStyle="compact"
                        iconOnly
                        allowedFileTypes="image/png, image/jpeg, image/jpg, image/webp"
                        parentCallback={(uploadedFiles) => {
                          const first = Array.isArray(uploadedFiles) ? uploadedFiles[0] : null;
                          const f = first?.file;
                          const url =
                            (f && typeof f.url === "string" && f.url) ||
                            (f?.thumb && typeof f.thumb.url === "string" && f.thumb.url) ||
                            "";
                          if (url) {
                            formik.setFieldValue("image_url", url);
                            dispatchWithToast(dispatch(updateUserAsync({ id: formik.values.id, image_url: url })));
                          }
                        }}
                      />
                    </div>
                    {formik.values.image_url && (
                      <button
                        type="button"
                        className="mt-2 text-xs text-red-600 hover:underline"
                        onClick={() => formik.setFieldValue("image_url", "")}
                      >
                        Remove image
                      </button>
                    )}
                  </div>
                  <div className="col-span-6 sm:col-span-4">
                    <PMField name="name" type="text" label="Name" {...formik} />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="email"
                      type="text"
                      label="Email"
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="mobile"
                      type="text"
                      label="Mobile"
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="mobile_work"
                      type="text"
                      label="Work Mobile"
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <DepartmentSelector
                      selected={item?.department}
                      {...formik}
                    />
                  </div>

                </div>
              </div>
            </div>

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="grid sm:grid-cols-1 gap-6">
                <div className="grid grid-cols-3 sm:grid-cols-3 bg-white sm:p-6 gap-6">
                  <div className="col-span-3">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      {t("user_info.address")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {t("user_info.address_hint")}
                    </p>
                  </div>
                  <div className="col-span-3 sm:col-span-3">
                    <PMField
                      name="postal_address.address"
                      type="text"
                      label="Address"
                      {...formik}
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-3">
                    <PMField
                      name="postal_address.address2"
                      type="text"
                      label="Address 2"
                      {...formik}
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-1">
                    <PMField
                      name="postal_address.postal"
                      type="text"
                      label="Postal"
                      placeName="postal_address.place"
                      {...formik}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-2">
                    <PMField
                      name="postal_address.place"
                      type="text"
                      label="Place"
                      {...formik}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="grid grid-cols-6 sm:grid-cols-6 bg-white sm:p-6 gap-6">
                <div className="col-span-3">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {t("Barn")}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500"></p>
                </div>
                <div className="col-span-6 sm:col-span-6">
                  <UserChildrenTable name="user_children" {...formik} />
                </div>
              </div>
            </div>

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="grid grid-cols-6 sm:grid-cols-6 bg-white sm:p-6 gap-6">
                <div className="col-span-3">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {t("Nærmeste")}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500"></p>
                </div>
                <div className="col-span-6 sm:col-span-6">
                  <UserNextOfKinTable name="user_next_of_kins" {...formik} />
                </div>
              </div>
            </div>

            {/* <PromptIfDirty {...formik}/> */}
            <FormikSaveBar {...formik} />
          </Form>
        )}
      </Formik>
    </>
  );
};
